import { useState, useRef, useMemo } from 'react';
import Slick from 'react-slick';
import Button from '@alltrails/shared/denali/components/Button';
import Texture from '@alltrails/shared/denali/components/Texture';
import { defineMessages } from '@alltrails/shared/react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import Slider from 'react-slick';
import useLanguageRegionCode from '@alltrails/shared/hooks/useLanguageRegionCode';
import DesktopFeatures from './DesktopFeatures';
import * as styles from './ExploreAndDiscover.module.scss';
import starRating from './star_rating.png';
import { wrapUrlSafe } from '../../../utils/language_support_util';
import { toATLocalString } from '../../../utils/number_util';
import { DESKTOP_BREAKPOINT, MEDIA_QUERY_SMALL_DOWN } from '../../../utils/constants/breakpoints';
import exploreWithConfidenceW2880JpgSrc from './explore-with-confidence-w2880.jpg';
import exploreWithConfidenceW2880AvifSrc from './explore-with-confidence-w2880.avif';
import exploreWithConfidenceW750JpgSrc from './explore-with-confidence-w750.jpg';
import exploreWithConfidenceW750AvifSrc from './explore-with-confidence-w750.avif';
import exploreWithConfidenceW750WebpSrc from './explore-with-confidence-w750.webp';
import exploreWithConfidenceW2880WebpSrc from './explore-with-confidence-w2880.webp';

const EXPLORE_AND_DISCOVER_STRINGS = defineMessages({
  EDITORS_CHOICE: {
    defaultMessage: 'Recipient of the App Store Editors’ Choice award'
  },
  EXPLORE_WITH_CONFIDENCE: {
    defaultMessage: 'Explore with confidence'
  },
  INSPIRATION: {
    defaultMessage: 'Inspiration and guidance for wherever your trail may lead.'
  },
  SEARCH_DISCOVER_NAV: {
    defaultMessage: 'Search & discover'
  },
  NAVIGATE_TRACK_NAV: {
    defaultMessage: 'Navigate & track'
  },
  REMEMBER_INSPIRE_NAV: {
    defaultMessage: 'Remember & inspire'
  },
  SEARCH_DISCOVER_TITLE: {
    defaultMessage: 'Pick the right trail for your day'
  },
  NAVIGATE_TRACK_TITLE: {
    defaultMessage: 'Navigate & track'
  },
  REMEMBER_INSPIRE_TITLE: {
    defaultMessage: 'Keep track of your travels'
  },
  SEARCH_DISCOVER_TEXT: {
    defaultMessage: 'All our trails are verified by experts and reviewed by our global community of adventurers like you.'
  },
  NAVIGATE_TRACK_TEXT: {
    defaultMessage: 'Out on the trail, you can keep an eye on your planned route, along with elevation changes and waypoints.'
  },
  REMEMBER_INSPIRE_TEXT: {
    defaultMessage: 'Log all your trails in one place, and share insights to inspire and inform fellow adventurers.'
  },
  EXPLORE_WITH_CONFIDENCE_ALT: {
    defaultMessage: 'Two cyclists walking their bikes across a wooden bridge.'
  },
  SIGNUP: {
    defaultMessage: 'Sign up'
  },
  RATING_IMAGE_ALT: {
    defaultMessage: '4.9 stars out of 5'
  }
});

const CDN_BASE = 'https://cdn-assets.alltrails.com/assets/images/homepage/explore_and_discover/';

type Props = {
  windowWidth: number;
};

const ExploreAndDiscover = ({ windowWidth }: Props): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();

  const {
    formattedDefaultMessages: {
      EDITORS_CHOICE,
      EXPLORE_WITH_CONFIDENCE,
      INSPIRATION,
      SEARCH_DISCOVER_NAV,
      NAVIGATE_TRACK_NAV,
      REMEMBER_INSPIRE_NAV,
      SEARCH_DISCOVER_TITLE,
      NAVIGATE_TRACK_TITLE,
      REMEMBER_INSPIRE_TITLE,
      SEARCH_DISCOVER_TEXT,
      NAVIGATE_TRACK_TEXT,
      REMEMBER_INSPIRE_TEXT,
      EXPLORE_WITH_CONFIDENCE_ALT,
      SIGNUP,
      RATING_IMAGE_ALT
    }
  } = useFormatMessage(EXPLORE_AND_DISCOVER_STRINGS);
  const isDesktop = useMemo(() => windowWidth && windowWidth >= DESKTOP_BREAKPOINT, [windowWidth]);

  const SLIDE_NAVS = [SEARCH_DISCOVER_NAV, NAVIGATE_TRACK_NAV, REMEMBER_INSPIRE_NAV];
  const SLIDE_TITLES = [SEARCH_DISCOVER_TITLE, NAVIGATE_TRACK_TITLE, REMEMBER_INSPIRE_TITLE];
  const SLIDE_TEXTS = [SEARCH_DISCOVER_TEXT, NAVIGATE_TRACK_TEXT, REMEMBER_INSPIRE_TEXT];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const carouselRef = useRef<Slider>();

  const navText = SLIDE_NAVS[currentSlide];
  const title = SLIDE_TITLES[currentSlide];
  const text = SLIDE_TEXTS[currentSlide];
  const carouselLength = 3;

  const slickSettings = {
    afterChange: (index: number) => {
      setCurrentSlide(index);
      setOpacity(1);
    },
    arrows: false,
    beforeChange: () => {
      setOpacity(0);
    }
  };

  const renderDots = () => (
    <div className={styles.dots}>
      {[...Array(carouselLength)].map((_, i) => (
        <button
          key={SLIDE_NAVS[i]}
          type="button"
          aria-label={`${SLIDE_NAVS[i]}`}
          className={`${styles.dot} ${currentSlide === i ? styles.active : ''}`}
          onClick={() => {
            carouselRef?.current?.slickGoTo(i);
          }}
        />
      ))}
    </div>
  );

  const renderMobileBottomContent = () => (
    <div className={styles.bottomContainer}>
      <Texture variant="leaves" className={styles.texture}>
        <div className={styles.carousel}>
          <Slick ref={carouselRef} {...slickSettings}>
            <div className={styles.slideContainer}>
              <picture>
                <source srcSet={`${CDN_BASE}rebrand_homepage-screens-search_discover_${languageRegionCode}.avif`} type="image/avif" />
                <img
                  alt={SEARCH_DISCOVER_NAV}
                  className={styles.slideImage}
                  src={`${CDN_BASE}rebrand_homepage-screens-search_discover_${languageRegionCode}.png`}
                  loading="lazy"
                />
              </picture>
            </div>
            <div className={styles.slideContainer}>
              <picture>
                <source srcSet={`${CDN_BASE}rebrand_homepage-screens-navigate_${languageRegionCode}.avif`} type="image/avif" />
                <img
                  alt={NAVIGATE_TRACK_NAV}
                  className={styles.slideImage}
                  src={`${CDN_BASE}rebrand_homepage-screens-navigate_${languageRegionCode}.png`}
                  loading="lazy"
                />
              </picture>
            </div>
            <div className={styles.slideContainer}>
              <picture>
                <source srcSet={`${CDN_BASE}rebrand_homepage-screens-remember_${languageRegionCode}.avif`} type="image/avif" />
                <img
                  alt={REMEMBER_INSPIRE_NAV}
                  className={styles.slideImage}
                  src={`${CDN_BASE}rebrand_homepage-screens-remember_${languageRegionCode}.png`}
                  loading="lazy"
                />
              </picture>
            </div>
          </Slick>
        </div>
      </Texture>
      {renderDots()}
      <div style={{ opacity }} className={styles.mobileContainer}>
        <h3 className={styles.nav}>{navText}</h3>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.featureText}>{text}</p>
        <div className={styles.signUpLink}>
          <Button
            text={SIGNUP}
            linkInfo={{ href: wrapUrlSafe('/signup?ref=homepage_signup_exploreanddiscover', languageRegionCode) }}
            testId="explore-and-discover-signup-link"
            variant="primary"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className={styles.topContainer}>
        <picture className={styles.picture}>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={exploreWithConfidenceW750AvifSrc} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={exploreWithConfidenceW750JpgSrc} />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={exploreWithConfidenceW750WebpSrc} type="image/webp" />
          <source srcSet={exploreWithConfidenceW2880AvifSrc} type="image/avif" />
          <source srcSet={exploreWithConfidenceW2880WebpSrc} type="image/webp" />
          <img alt={EXPLORE_WITH_CONFIDENCE_ALT} className={styles.img} loading="lazy" src={exploreWithConfidenceW2880JpgSrc} />
        </picture>
        <div className={styles.content}>
          <h1 className={styles.titleTop}>{EXPLORE_WITH_CONFIDENCE}</h1>
          <p className={styles.text}>{INSPIRATION}</p>
          <div className={styles.badgeStarContainer}>
            <img
              className={styles.badgeImage}
              src={`https://cdn-assets.alltrails.com/assets/images/homepage/editors_choice/rebrand-apple-editor-badge-${languageRegionCode}.svg`}
              alt={EDITORS_CHOICE}
              loading="lazy"
            />
            <span className={styles.rating}>{toATLocalString(4.9, languageRegionCode, 1)}</span>

            <img alt={RATING_IMAGE_ALT} src={starRating} className={styles.starsImage} loading="lazy" />
          </div>
        </div>
      </div>
      {isDesktop ? <DesktopFeatures /> : renderMobileBottomContent()}
    </div>
  );
};

export default ExploreAndDiscover;
